import AccessTimeFilledRoundedIcon from "@mui/icons-material/AccessTimeFilledRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import ExpandLessRoundedIcon from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRoundedIcon from "@mui/icons-material/ExpandMoreRounded";
import HourglassTopRoundedIcon from "@mui/icons-material/HourglassTopRounded";
import PhoneIphoneOutlinedIcon from "@mui/icons-material/PhoneIphoneOutlined";
import SupportAgentRoundedIcon from "@mui/icons-material/SupportAgentRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import Alert from "@mui/material/Alert";
import Avatar from "@mui/material/Avatar";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import LinearProgress from "@mui/material/LinearProgress";
import Stack from "@mui/material/Stack";
import Typography from "@mui/material/Typography";
import React from "react";

export type ProcessStatusType = "processing" | "success" | "failed" | "delayed";

export interface ProcessStatusDetailItem {
  label: string;
  value?: React.ReactNode;
  fullWidth?: boolean;
}

export interface ProcessStatusPanelProps {
  status: ProcessStatusType;
  title?: string;
  processingTitle?: string;
  successTitle?: string;
  failedTitle?: string;
  delayedTitle?: string;
  processingMessage?: string;
  successMessage?: string;
  failedMessage?: string;
  delayedMessage?: string;
  processingWarningMessage?: string;
  onClose?: () => void;
  onPrimaryAction?: () => void;
  primaryActionLabel?: string;
  details?: ProcessStatusDetailItem[];
  /** Use `embedded` inside checkout layouts; `dialog` when wrapped by Dialog. */
  variant?: "dialog" | "embedded";
}

const renderInlineDetailPairs = (value: string) => {
  const chunks = value
    .split("|")
    .map((item) => item.trim())
    .filter(Boolean);

  if (!chunks.length) return value;

  return chunks.map((chunk, index) => {
    const colonIndex = chunk.indexOf(":");
    if (colonIndex === -1) {
      return (
        <React.Fragment key={`${chunk}-${index}`}>
          {index > 0 ? " | " : ""}
          {chunk}
        </React.Fragment>
      );
    }

    const keyPart = chunk.slice(0, colonIndex + 1);
    const valuePart = chunk.slice(colonIndex + 1).trim();

    return (
      <React.Fragment key={`${chunk}-${index}`}>
        {index > 0 ? " | " : ""}
        <Box component="span" sx={{ color: "text.secondary", fontWeight: 500 }}>
          {keyPart}
        </Box>{" "}
        <Box component="span" sx={{ color: "text.primary", fontWeight: 700 }}>
          {valuePart}
        </Box>
      </React.Fragment>
    );
  });
};

const ProcessStatusPanel: React.FC<ProcessStatusPanelProps> = ({
  status,
  title,
  processingTitle = "Processing request",
  successTitle = "Completed successfully",
  failedTitle = "Request failed",
  delayedTitle = "Request still processing",
  processingMessage = "Please wait while we process your request.",
  successMessage = "Your request has been completed successfully.",
  failedMessage = "We could not complete your request right now. Please try again.",
  delayedMessage = "This is taking longer than expected. Please wait and check again soon.",
  processingWarningMessage = "Please do not press back, close this window, or refresh the page while we confirm your recharge.",
  onClose,
  onPrimaryAction,
  primaryActionLabel = "Contact Support",
  details = [],
  variant = "dialog",
}) => {
  const [showAllDetails, setShowAllDetails] = React.useState<boolean>(false);

  React.useEffect(() => {
    setShowAllDetails(false);
  }, [status]);

  const resolvedTitle =
    title ||
    (status === "processing"
      ? processingTitle
      : status === "success"
        ? successTitle
        : status === "delayed"
          ? delayedTitle
          : failedTitle);

  const resolvedMessage =
    status === "processing"
      ? processingMessage
      : status === "success"
        ? successMessage
        : status === "delayed"
          ? delayedMessage
          : failedMessage;

  const accentColor =
    status === "processing"
      ? "#1976d2"
      : status === "success"
        ? "#2e7d32"
        : status === "delayed"
          ? "#ed6c02"
          : "#d32f2f";

  const headerIcon =
    status === "processing" ? (
      <HourglassTopRoundedIcon />
    ) : status === "success" ? (
      <CheckCircleRoundedIcon />
    ) : status === "delayed" ? (
      <AccessTimeFilledRoundedIcon />
    ) : (
      <ErrorRoundedIcon />
    );

  const visibleDetails = details.slice(0, 2);

  const inner = (
    <Box
      sx={{
        background: "#ffffff",
      }}
    >
      <Box
        sx={{
          px: { xs: 2, sm: 3 },
          pt: { xs: 1.75, sm: 2.25 },
          pb: 1.5,
          borderBottom: "1px solid rgba(2,6,23,0.08)",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              sx={{
                width: 42,
                height: 42,
                bgcolor: `${accentColor}18`,
                color: "#fff",
                border: `1px solid ${accentColor}44`,
              }}
            >
              <Box sx={{ color: accentColor, display: "flex" }}>
                {headerIcon}
              </Box>
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800}>
                {resolvedTitle}
              </Typography>
            </Box>
          </Box>
        </Box>
      </Box>

      <Box p={{ xs: 2, sm: 3 }}>
        <Stack spacing={1.5}>
          {status === "processing" ? (
            <>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  mb: 0.5,
                }}
              >
                <Typography variant="body2" fontWeight={700}>
                  Verifying transaction status
                </Typography>
              </Box>
              <LinearProgress
                sx={{
                  borderRadius: 99,
                  height: 6,
                  bgcolor: "rgba(25,118,210,0.12)",
                  "& .MuiLinearProgress-bar": { bgcolor: accentColor },
                }}
              />
            </>
          ) : null}

          {details.length ? (
            <Box
              sx={{
                borderRadius: 1.5,
                border: "1px solid rgba(2,6,23,0.08)",
                p: { xs: 1, sm: 1.1 },
              }}
            >
              <Typography
                variant="subtitle2"
                fontWeight={800}
                color="text.primary"
                mb={0.6}
              >
                Transaction Snapshot
              </Typography>
              <Box
                sx={{
                  py: 0.75,
                  px: 1,
                  border: "1px solid #d7dbe7",
                  borderRadius: 1.25,
                  bgcolor: "#fbfbfd",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                  gap: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                  <Avatar
                    sx={{
                      width: 22,
                      height: 22,
                      bgcolor: "rgba(37,99,235,0.10)",
                      color: "#2563eb",
                    }}
                  >
                    <PhoneIphoneOutlinedIcon sx={{ fontSize: 13 }} />
                  </Avatar>
                  <Box>
                    <Typography
                      variant="caption"
                      sx={{
                        color: "text.disabled",
                        fontSize: 12,
                        fontWeight: 400,
                      }}
                    >
                      {visibleDetails[0]?.label || "Phone Number"}
                    </Typography>
                    <Typography
                      variant="body1"
                      sx={{ fontWeight: 600, fontSize: 14, lineHeight: 1.1 }}
                    >
                      {visibleDetails[0]?.value || "-"}
                    </Typography>
                  </Box>
                </Box>

                <Box sx={{ display: "flex", alignItems: "center", gap: 0.4 }}>
                  <Typography
                    variant="body1"
                    sx={{ fontWeight: 800, fontSize: 16, lineHeight: 1.1 }}
                  >
                    {visibleDetails[1]?.value || "-"}
                  </Typography>
                  {details.length > 2 ? (
                    <IconButton
                      size="small"
                      onClick={() => setShowAllDetails((prev) => !prev)}
                    >
                      {showAllDetails ? (
                        <ExpandLessRoundedIcon />
                      ) : (
                        <ExpandMoreRoundedIcon />
                      )}
                    </IconButton>
                  ) : null}
                </Box>
              </Box>
              {showAllDetails ? (
                <Grid container spacing={0.75} pt={0.6}>
                  {details.slice(2).map((item, index) => (
                    <Grid
                      item
                      xs={12}
                      sm={item.fullWidth ? 12 : 6}
                      key={`${item.label}-expanded-${index}`}
                    >
                      <Box
                        sx={{
                          py: 0.75,
                          px: 1,
                          border: "1px solid rgba(2,6,23,0.12)",
                          borderRadius: 1.5,
                          bgcolor: "#fff",
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{
                            color: "text.disabled",
                            fontWeight: 400,
                            fontSize: 12,
                            letterSpacing: "0.02em",
                            textTransform: "uppercase",
                          }}
                        >
                          {item.label}
                        </Typography>
                        <Typography
                          variant="body1"
                          sx={{
                            mt: 0.2,
                            fontWeight: item.fullWidth ? 500 : 700,
                            color: "text.primary",
                            fontSize: 15,
                            wordBreak: "break-word",
                          }}
                        >
                          {typeof item.value === "string" && item.fullWidth
                            ? renderInlineDetailPairs(item.value)
                            : item.value || "-"}
                        </Typography>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              ) : null}
            </Box>
          ) : null}

          {status !== "processing" ? (
            <Box
              sx={{
                borderRadius: 1.5,
                border: `1px solid ${
                  status === "success"
                    ? "rgba(46,125,50,0.25)"
                    : status === "failed"
                      ? "rgba(211,47,47,0.22)"
                      : "rgba(237,108,2,0.22)"
                }`,
                bgcolor:
                  status === "success"
                    ? "rgba(46,125,50,0.06)"
                    : status === "failed"
                      ? "rgba(211,47,47,0.06)"
                      : "rgba(237,108,2,0.07)",
                p: 1.25,
              }}
            >
              <Box
                sx={{ display: "flex", alignItems: "flex-start", gap: 1.25 }}
              >
                {status === "success" ? (
                  <CheckCircleRoundedIcon
                    sx={{ color: "#2e7d32", mt: "2px", fontSize: 20 }}
                  />
                ) : status === "failed" ? (
                  <ErrorRoundedIcon
                    sx={{ color: "#d32f2f", mt: "2px", fontSize: 20 }}
                  />
                ) : (
                  <AccessTimeFilledRoundedIcon
                    sx={{ color: "#ed6c02", mt: "2px", fontSize: 20 }}
                  />
                )}
                <Typography variant="body1" fontWeight={500}>
                  {resolvedMessage}
                </Typography>
              </Box>
            </Box>
          ) : (
            <Alert
              icon={<WarningAmberRoundedIcon fontSize="inherit" />}
              severity="warning"
              sx={{
                py: 0.2,
                bgcolor: "warning.light",
                color: "warning.dark",
                border: "1.5px solid",
                borderColor: "warning.main",
                borderRadius: 1.5,
                "& .MuiAlert-message": { fontSize: 15, fontWeight: 500 },
                "& .MuiAlert-icon": { color: "warning.dark" },
              }}
            >
              {processingWarningMessage}
            </Alert>
          )}
        </Stack>
      </Box>

      {status !== "processing" ? (
        <Box
          sx={{
            px: { xs: 2, sm: 3 },
            py: 1.4,
            borderTop: "1px solid rgba(2,6,23,0.08)",
            display: "flex",
            justifyContent: "flex-end",
            gap: 1,
            flexWrap: "wrap",
            bgcolor: "#fafafa",
          }}
        >
          {onPrimaryAction ? (
            <Button
              variant="contained"
              onClick={onPrimaryAction}
              startIcon={<SupportAgentRoundedIcon />}
            >
              {primaryActionLabel}
            </Button>
          ) : null}
          {onClose ? (
            <Button variant="outlined" color="inherit" onClick={onClose}>
              Close
            </Button>
          ) : null}
        </Box>
      ) : null}
    </Box>
  );

  if (variant === "embedded") {
    return (
      <Box
        sx={{
          borderRadius: 2,
          overflow: "hidden",
          border: "1px solid rgba(2,6,23,0.12)",
          boxShadow: "0 4px 18px rgba(2, 6, 23, 0.08)",
        }}
      >
        {inner}
      </Box>
    );
  }

  return inner;
};

export default ProcessStatusPanel;
